/**
 * MAIN OPTIMIZED - Entry Point Ottimizzato FantAPP
 * Sostituisce il main.js mostruoso (7,988 righe) con architettura modulare
 */

// Import moduli ottimizzati
import { eventManager, Events } from "./core/events.js";
import { log } from "./utils/logger.js";
import { filtersManager, getRoleFilterChipClass } from "./features/filters/index.js";
import { TableManager } from "./features/table/index.js";
// Import servizi esistenti (già ottimizzati)
import { PlayerStatsService } from "./services/PlayerStatsService.js";
import { UserSession, SessionPersistence } from "./services/SessionPersistence.js";
import { testPocketBaseConnection } from "./services/pocketbase.js";
import { UserAccessScreen } from "./ui/UserAccessScreen.js";

// Import UI esistenti
import "./ui/CustomDialogs.js";
import { initLongPressLabels } from "./ui/longPressLabel.js";

// Player key utility
import { makePlayerKey, parsePlayerKey, normalizePlayerKey, playerToKey } from "./utils/playerKey.js";

// Liste giocatori su PocketBase
import { savePlayerList, loadPlayerList } from "./services/db/lists.pocketbase.js";

// Gruppi di metodi estratti dalla classe FantAPP (agganciati al prototype piu sotto)
import * as fileMessages from "./main/fileMessages.js";
import * as filtersChrome from "./main/filtersChrome.js";
import * as auctionModeChrome from "./main/auctionModeChrome.js";
import * as appShell from "./main/appShell.js";
import { persistenceMethods } from "./main/persistence.js";
import { demoDebugMethods } from "./main/demoDebug.js";
import { studioMethods } from "./main/studio.js";
import { studioViewMethods } from "./main/studioView.js";
import { rosaMethods } from "./main/rosa.js";
import { rosaViewMethods } from "./main/rosaView.js";
import { roseAltriViewMethods } from "./main/roseAltriView.js";
import { analyticsViewMethods } from "./main/analyticsView.js";
import { auctionSessionMethods } from "./main/auctionSession.js";
import { recentTakesMethods } from "./main/recentTakes.js";
import { listaKpiMethods } from "./main/listaKpi.js";
import {
  DEFAULT_ROSA_MAX_PLAYERS,
  DEFAULT_ROSA_ROLE_TARGETS,
} from "./services/db/rosaSettings.pocketbase.js";
import { playerDataMethods } from "./main/playerData.js";
import { auctionSetupMethods } from "./main/auctionSetup.js";
import { auctionTableMethods } from "./main/auctionTable.js";
import { userDataMethods } from "./main/userData.js";
import { bootViewMethods } from "./main/bootViews.js";
import { ROSE_ALTRI_UNASSIGNED_ID } from "./main/roseAltriRoster.js";

class FantAPP {
  constructor() {
    this.state = {
      currentView: "user-access",
      userSession: null,
      playerStatsService: null,
      isInitialized: false,

      // Dati giocatori
      allPlayers: [],
      playersHydrated: [], // Per compatibilità con i test

      studioTargets: new Map(),
      studioBudget: 500,
      /** @type {string[]} catalogo etichette custom (access_key) */
      studioUserLabels: [],
      studioUserLabelColors: {},
      /** @type {Map<string, { maxPrice: number, notes: string, labels: string[] }>} bozze Svincolati senza slot */
      studioDrafts: new Map(),
      studioLabelFilter: null,
      studioRoleFilter: null,
      studioTeamFilter: null,
      studioBandFilter: null,
      studioCompareModalOpen: false,
      studioCompareSlots: [null, null, null, null, null],

      /** @type {Map<string, { price: number }>} */
      roster: new Map(),
      /** @type {Map<string, { price: number, at?: number, opponentId?: string|null }>} */
      auctionExits: new Map(),
      activeAuctionId: null,
      activeAuctionName: '',
      rosaBudget: 500,
      rosaRoleTargets: { ...DEFAULT_ROSA_ROLE_TARGETS },
      rosaMaxPlayers: DEFAULT_ROSA_MAX_PLAYERS,
      rosaNumParticipants: null,
      /** @type {'simple'|'assigned'} */
      rosaExitMode: 'simple',
      /** @type {{ id: string, name: string }[]} */
      auctionOpponents: [],
      rosaSelectedModule: "4-3-3",
      /** @type {string} 'unassigned' | id avversario */
      roseAltriSelectedOpponentId: ROSE_ALTRI_UNASSIGNED_ID,
      roseAltriSelectedModule: "4-3-3",
      rosaSettingsError: null,
      /** @type {'mantra'|'classic'} */
      analyticsRoleMode: "mantra",
      analyticsTrendRoleFilter: null,
      /** @type {{ from: number, to: number, total: number }|null} */
      analyticsGlobalTurnRange: null,
      /** @type {'me'|string} me | opponent id */
      analyticsWhoFilter: 'me',

      /** @type {'prep'|'live'} */
      auctionMode: "prep",
    };

    // Managers
    this.tableManager = null;
    this.userAccessScreen = null;

    // Expose globally per compatibilità
    window.appState = this.state;
    window.state = this.state; // Alias per test
    window.fantApp = this;
    window.app = this; // Riferimento per filtri
    
    // Funzioni demo per test rapidi
    window.injectDemo = () => this.injectDemoPlayers();
    window.demoHelp = () => this.showDemoHelp();
    window.testAutoReload = () => this.testAutoReloadFunction();

    log.info("FantAPP: Inizializzazione...");
  }

  /**
   * Verifica connessione PocketBase
   */
  async initializeBackend() {
    const result = await testPocketBaseConnection();
    if (result.success) {
      log.info("PocketBase: connessione OK");
      this.state.backendStatus = "connected";
    } else {
      log.warn("PocketBase non raggiungibile (modalità offline possibile):", result.error);
      this.state.backendStatus = "error";
      this.state.backendError = result.error;
    }
  }

  /**
   * Inizializza servizi
   */
  async initializeServices() {
    // User Session
    this.state.userSession = new UserSession();

    // Player Stats Service
    this.state.playerStatsService = new PlayerStatsService(
      this.state.userSession
    );

  }

  /**
   * Inizializza UI (SEMPLIFICATO - Bootstrap gestisce il resto)
   */
  async initializeUI() {
    // User Access Screen
    this.userAccessScreen = new UserAccessScreen(
      this.state.userSession,
      (user) => {
        log.info("Login: Successo per", user.display_name);
        
        // *** V2: Salva sessione in SessionPersistence ***
        SessionPersistence.saveSession(user);
        
        this.reloadUserData().catch((err) => {
          log.warn('Errore caricamento dati post-login:', err);
        });
        
        // Vai alla vista appropriata
        this.showAuctionView({ restore: false, initialView: 'auto' });
      }
    );
    this.userAccessScreen.initialize();
    
    // NOTA: Il bootstrap gestisce il ripristino sessione e la scelta vista
    log.info("UI: UserAccessScreen inizializzato, bootstrap gestirà le viste");
  }

  /**
   * Setup event listeners globali
   */
  setupEventListeners() {
    // File upload con feedback visuale
    const fileInput = document.getElementById("sv-file");
    if (fileInput) {
      fileInput.addEventListener("change", (e) => {
        const file = e.target.files[0];
        if (file) {
          this.showFileInfo(file);
          this.processExcelFile(file);
        }
      });
    }

    // Start auction button
    const startButton = document.getElementById("start-auction-btn");
    if (startButton) {
      startButton.addEventListener("click", () => this.startAuction());
    }

    // Pulsante: aggiorna quotazioni da Fantacalcio (online)
    const onlineBtn = document.getElementById("load-online-btn");
    if (onlineBtn) {
      onlineBtn.addEventListener("click", () => this.loadQuotazioniOnline());
    }

    // Caricamento rapido dati demo con Alt+D
    document.addEventListener('keydown', (e) => {
      if (e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        this.handleQuickDemoLoad();
      }
    });

    // Search input
    const searchInput = document.querySelector(".player-search");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        filtersManager.setFilter("search", e.target.value.toLowerCase().trim());
        this.syncFiltersChrome();
      });
    }

    this.setupFiltersChrome();
    this.setupAuctionModeChrome();
    this.setupRecentTakesStrip?.();
    this.setupAppShell();

    // Table sorting
    this.setupTableSorting();

    document.getElementById('open-studio-btn')?.addEventListener('click', () => this.switchAuctionScreen('studio'));
    document.getElementById('studio-back-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
    document.getElementById('open-rosa-btn')?.addEventListener('click', () => this.switchAuctionScreen('rosa'));
    document.getElementById('rosa-back-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
    document.getElementById('rose-altri-back-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
    document.getElementById('open-analytics-btn')?.addEventListener('click', () => this.switchAuctionScreen('analytics'));
    document.getElementById('analytics-back-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
    document.getElementById('studio-manage-labels-btn')?.addEventListener('click', () => this.openStudioLabelsModal());
    document.getElementById('auction-settings-close')?.addEventListener('click', () => this.closeAuctionSettings?.());
    document.getElementById('auction-settings')?.addEventListener('click', (e) => {
      if (e.target?.id === 'auction-settings') this.closeAuctionSettings?.();
    });

    if (!this._studioTableClickBound) {
      this._studioTableClickBound = true;
      document.querySelector('.players-table-container')?.addEventListener('click', (e) => {
        const presoBtn = e.target.closest('[data-action="preso"], .preso-btn--assign');
        if (presoBtn && !presoBtn.disabled) {
          const name = presoBtn.dataset.playerName;
          const team = presoBtn.dataset.playerTeam;
          const key = presoBtn.dataset.playerKey || (name && team ? playerToKey({ name, team }) : null);
          if (key) this.showPresoForm(key, { name, team }, presoBtn, { mode: 'me' });
          return;
        }
        const btn = e.target.closest('.studio-add-btn');
        if (!btn) return;
        const name = btn.dataset.playerName;
        const team = btn.dataset.playerTeam;
        if (!name || !team) return;
        const key = playerToKey({ name, team });
        const row = btn.closest('tr') || btn.closest('.player-card');
        this.showStudioInlineForm(key, row || btn);
      });
    }

    if (!this._undoExitClickBound) {
      this._undoExitClickBound = true;
      document.getElementById('auction-main-interface')?.addEventListener('click', (e) => {
        const editAssignmentBtn = e.target.closest(
          '[data-action="edit-assignment"], [data-action="edit-exit"], .preso-btn--exit-edit, .preso-btn--assigned',
        );
        if (editAssignmentBtn) {
          const name = editAssignmentBtn.dataset.playerName;
          const team = editAssignmentBtn.dataset.playerTeam;
          const key = editAssignmentBtn.dataset.playerKey || (name && team ? playerToKey({ name, team }) : null);
          if (!key) return;
          const side = editAssignmentBtn.dataset.assignmentSide
            || (editAssignmentBtn.dataset.action === 'edit-exit' ? 'others' : null)
            || (this.state.roster?.has?.(key) ? 'me' : 'others');
          if (side === 'me') {
            const roster = this.state.roster?.get?.(key);
            this.showPresoForm(key, { name, team }, editAssignmentBtn, {
              mode: 'me',
              initialPrice: roster?.price ?? null,
              allowUndo: true,
            });
          } else {
            const exit = this.state.auctionExits?.get?.(key);
            this.showPresoForm(key, { name, team }, editAssignmentBtn, {
              mode: 'others',
              opponentId: exit?.opponentId ?? null,
              initialPrice: exit?.price ?? null,
              allowUndo: true,
            });
          }
          return;
        }
        const undoExitBtn = e.target.closest('[data-action="undo-exit"]');
        if (!undoExitBtn) return;
        const name = undoExitBtn.dataset.playerName;
        const team = undoExitBtn.dataset.playerTeam;
        const key = undoExitBtn.dataset.playerKey || (name && team ? playerToKey({ name, team }) : null);
        if (key) void this.unmarkAuctionExited?.(key);
      });
    }

    if (!this._studioTableUpdateBound) {
      this._studioTableUpdateBound = true;
      // TABLE_UPDATE è prima del render (queueRender → rAF). I controlli Studio
      // vanno montati dopo TABLE_RENDER, quando tbody/card sono già nel DOM.
      eventManager.on(Events.TABLE_UPDATE, () => {
        this.hideStudioInlineForm();
        this._closeOtherStudioRowPopovers?.(null);
      });
      eventManager.on(Events.TABLE_RENDER, () => {
        this.syncStudioButtonsInTable();
      });
    }
    
    log.info('Event listeners: Setup completato (incluso Alt+D)');
  }

  /**
   * Gestisce il caricamento rapido demo con Alt+D
   */
  handleQuickDemoLoad() {
    try {
      log.info('🚀 Caricamento rapido demo attivato (Alt+D)');
      
      // Verifica se ci sono già dati caricati
      if (this.state.allPlayers.length > 0) {
        const confirmOverwrite = window.confirm(
          `Sono già presenti ${this.state.allPlayers.length} giocatori.\n\n` +
          'Vuoi sostituirli con i dati demo?'
        );
        
        if (!confirmOverwrite) {
          log.info('Caricamento demo annullato dall\'utente');
          return;
        }
      }
      
      // Carica i dati demo
      const demoPlayers = this.injectDemoPlayers();
      
      // Mostra notifica di successo
      if (window.showToast) {
        window.showToast(
          `✅ ${demoPlayers.length} giocatori demo caricati rapidamente!`, 
          'success'
        );
      } else {
        console.log(`✅ ${demoPlayers.length} giocatori demo caricati con Alt+D`);
      }
      
      // *** V2: Se siamo nella schermata di setup, passa alla vista principale con persistenza ***
      const setupScreen = document.getElementById('auction-setup-screen');
      const mainInterface = document.getElementById('auction-main-interface');
      
      if (setupScreen && !setupScreen.hidden) {
        log.info('Passaggio automatico alla vista principale dopo demo load con persistenza');
        // Usa showAuctionView per persistenza immediata
        this.showAuctionView({ restore: false, initialView: 'main' });
      }
      
    } catch (error) {
      log.error('Errore caricamento rapido demo:', error);
      if (window.showToast) {
        window.showToast('❌ Errore durante il caricamento demo', 'error');
      }
    }
  }

  /**
   * Setup ordinamento tabella
   */
  setupTableSorting() {
    document.addEventListener("click", (e) => {
      const header = e.target.closest("th[data-sortable]");
      if (!header) return;

      const column = header.dataset.column;
      eventManager.emit(Events.TABLE_SORT, { column });
    });
  }
  
  /**
   * Passa dalla schermata di setup all'interfaccia principale
   */
  switchToMainInterface() {
    console.log('[AUCTION_VIEW] 🔄 switchToMainInterface chiamato');
    this.showMainInterface();
  }

  /**
   * Mostra messaggio di benvenuto con tips
   */
  showWelcomeMessage() {
    if (this.state.allPlayers.length > 0) {
      // Se ci sono già dati caricati, non mostrare il messaggio
      return;
    }
    
    console.log(
      `%c🎆 FantAPP avviata con successo! 🎆`,
      'color: #28a745; font-weight: bold; font-size: 16px;'
    );
    
    console.log(
      `%c💡 SUGGERIMENTO: Premi Alt+D per caricare rapidamente 20 giocatori demo e iniziare subito!`,
      'color: #ffc107; font-weight: bold; background: #fff3cd; padding: 4px 8px; border-radius: 4px;'
    );
    
    console.log(
      `%c⚙️ Altri comandi utili: demoHelp() | window.diag()`,
      'color: #6c757d; font-style: italic;'
    );
  }

  /**
   * Setup controlli paginazione
   */
  setupPaginationControls() {
    document.querySelectorAll('[data-testid="pagination-prev"]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        this.tableManager?.prevPage();
      });
    });

    document.querySelectorAll('[data-testid="pagination-next"]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        this.tableManager?.nextPage();
      });
    });

    log.info('Controlli paginazione: Configurati');
  }


  /**
   * Aggiorna l'header utente con le informazioni corrette
   */
  updateUserHeader() {
    try {
      const nameEl = document.querySelector('[data-testid="user-name"]');
      if (!nameEl) {
        console.warn('[main.js] ⚠️ Elemento user-name non trovato nell\'header');
        return;
      }
      
      // Ottieni informazioni utente corrente
      const currentUser = this.state.userSession?.getCurrentUser?.();
      if (!currentUser) {
        console.warn('[main.js] ⚠️ Utente corrente non disponibile');
        nameEl.textContent = 'Utente';
        return;
      }
      
      // Usa display_name se disponibile, altrimenti access_code
      const displayName = currentUser.display_name || currentUser.access_code || 'Utente';
      nameEl.textContent = displayName;
      
      console.log('[main.js] ✅ Header utente aggiornato:', displayName);
    } catch (error) {
      console.error('[main.js] ❌ Errore aggiornamento header utente:', error);
    }
  }

}

// Aggancia i gruppi di metodi estratti al prototype della classe FantAPP
Object.assign(FantAPP.prototype, fileMessages);
Object.assign(FantAPP.prototype, filtersChrome);
Object.assign(FantAPP.prototype, auctionModeChrome);
Object.assign(FantAPP.prototype, appShell);
Object.assign(FantAPP.prototype, persistenceMethods);
Object.assign(FantAPP.prototype, demoDebugMethods);
Object.assign(FantAPP.prototype, studioMethods);
Object.assign(FantAPP.prototype, studioViewMethods);
Object.assign(FantAPP.prototype, auctionSessionMethods);
Object.assign(FantAPP.prototype, recentTakesMethods);
Object.assign(FantAPP.prototype, listaKpiMethods);
Object.assign(FantAPP.prototype, rosaMethods);
Object.assign(FantAPP.prototype, rosaViewMethods);
Object.assign(FantAPP.prototype, roseAltriViewMethods);
Object.assign(FantAPP.prototype, analyticsViewMethods);
Object.assign(FantAPP.prototype, playerDataMethods);
Object.assign(FantAPP.prototype, auctionTableMethods);
Object.assign(FantAPP.prototype, userDataMethods);
Object.assign(FantAPP.prototype, bootViewMethods);
Object.assign(FantAPP.prototype, auctionSetupMethods);

// Inizializza app quando DOM è pronto - NUOVO BOOTSTRAP ASINCRONO
document.addEventListener("DOMContentLoaded", async () => {
  const app = new FantAPP();
  initLongPressLabels('.studio-card-actions');
  initLongPressLabels('.studio-list-actions--quick');

  // USA BOOTSTRAP ASINCRONO per evitare flicker login → asta
  await app.bootstrap();
  
  // Esponi app globalmente per debug
  window.fantApp = app;
});

// Esponi globalmente per debug
window.FantAPP = FantAPP;

// *** AGGIORNAMENTO DIAGNOSTICA V2 ***
window.diag = function () {
  const persistence = SessionPersistence;
  return {
    session: persistence.getSession(),
    view: persistence.getView(),
    playersMeta: persistence.getPlayersMeta(),
    playersCount: persistence.loadPlayers().length,
    version: sessionStorage.getItem('fantapp:v2:version'),
    storageUsed: persistence._getStorageUsage(),
    hasActiveSession: persistence.hasActiveSession(),
    hasPlayers: persistence.hasPlayers()
  };
};

if (typeof window !== 'undefined') {
  window.injectDemo = () => {
    const app = window.fantApp;
    if (app?.injectDemoPlayers) return app.injectDemoPlayers();
    console.error('❌ App non disponibile per demo');
    return null;
  };

  window.demoHelp = () => {
    const app = window.fantApp;
    if (app?.showDemoHelp) return app.showDemoHelp();
    console.log('🚀 Comandi: injectDemo() | window.diag() | demoHelp()');
    return null;
  };
}
